function createTaskElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = text;
  return element;
}

function createTaskTable(headers, rows, emptyMessage) {
  const table = createTaskElement('table', 'cc-table');
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  headRow.append(...headers.map((header) => createTaskElement('th', '', header)));
  head.append(headRow);
  const body = document.createElement('tbody');
  if (rows.length) {
    body.append(...rows);
  } else {
    const row = document.createElement('tr');
    const cell = createTaskElement('td', 'muted', emptyMessage);
    cell.colSpan = headers.length;
    row.append(cell);
    body.append(row);
  }
  table.append(head, body);
  const wrapper = createTaskElement('div', 'cc-table-scroll');
  wrapper.append(table);
  return wrapper;
}

function createTaskMetric(label, value, foot, valueClass = '') {
  const card = createTaskElement('div', 'cc-card');
  card.append(
    createTaskElement('div', 'label', label),
    createTaskElement('div', `value${valueClass ? ` ${valueClass}` : ''}`, value),
    createTaskElement('div', 'foot', foot),
  );
  return card;
}

function createTaskFilter(filters, total) {
  const filter = createTaskElement('div', 'cc-filters');
  const status = createTaskElement('select', 'filter-select');
  status.id = 'cc-task-status';
  status.setAttribute('aria-label', '任务状态');
  [
    ['', '全部状态'],
    ['queued', '排队中'],
    ['running', '执行中'],
    ['completed', '已完成'],
    ['failed', '失败'],
  ].forEach(([value, label]) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    option.selected = filters.status === value;
    status.append(option);
  });
  const search = createTaskElement('input', 'search-input');
  search.type = 'search';
  search.id = 'cc-task-search';
  search.placeholder = '任务编号、用户、邮箱或 ASIN';
  search.value = filters.search || '';
  const apply = createTaskElement('button', 'btn btn-primary', '查找任务');
  apply.type = 'button';
  apply.dataset.ccTaskAction = 'apply';
  const reset = createTaskElement('button', 'btn btn-secondary', '重置');
  reset.type = 'button';
  reset.dataset.ccTaskAction = 'reset';
  filter.append(status, search, apply, reset, createTaskElement('span', 'muted', `当前结果 ${Number(total || 0)} 条`));
  return filter;
}

function createRecentJobRow(job, { statusLabel, phaseLabel, formatCcTime }) {
  const status = String(job.status || 'unknown');
  const tagClass = status === 'completed' ? 'ok' : status === 'failed' ? 'bad' : 'warn';
  const row = document.createElement('tr');
  const phase = createTaskElement('td');
  phase.append(
    createTaskElement('strong', '', phaseLabel(job.phase)),
    createTaskElement('div', 'mono', job.phase || '—'),
  );
  const diagnostics = createTaskElement('button', 'btn btn-sm', '诊断');
  diagnostics.type = 'button';
  diagnostics.dataset.ccJobDiagnostics = String(job.id || '');
  const tag = createTaskElement('span', `cc-tag ${tagClass}`, statusLabel(status));
  const statusCell = document.createElement('td');
  statusCell.append(tag);
  const actionCell = document.createElement('td');
  actionCell.append(diagnostics);
  row.append(
    createTaskElement('td', 'mono', String(job.id || '')),
    createTaskElement('td', '', job.userName || job.userEmail || String(job.userId || '')),
    statusCell,
    phase,
    createTaskElement('td', '', job.marketplace || '—'),
    createTaskElement('td', '', formatCcTime(job.updatedAt || job.createdAt)),
    actionCell,
  );
  return row;
}

function createStuckTaskRow(job, { phaseLabel }) {
  const row = document.createElement('tr');
  const inMemory = createTaskElement('td', '', job.inMemory ? '是' : '否');
  if (!job.inMemory) {
    inMemory.replaceChildren(createTaskElement('span', 'delta-warn', '否'));
  }
  row.append(
    createTaskElement('td', 'mono', String(job.id || '')),
    createTaskElement('td', '', job.userName || String(job.userId || '')),
    createTaskElement('td', '', phaseLabel(job.phase)),
    createTaskElement('td', '', `${job.idleMinutes ?? '—'} 分钟`),
    inMemory,
  );
  return row;
}

export function renderCommandCenterTasks(root, data, filters, helpers) {
  if (!root) return;
  const batch = data.batchJobs || {};
  const counts = batch.counts7d || {};
  const queue = data.imageQueue || {};
  const active = Number(counts.running || 0) + Number(counts.queued || 0);
  const recentRows = (batch.rows || []).map((job) => createRecentJobRow(job, helpers));
  const stuckRows = (batch.stuck || []).map((job) => createStuckTaskRow(job, helpers));
  const metrics = createTaskElement('div', 'cc-grid-4');
  metrics.append(
    createTaskMetric('7 天活跃批量任务', active, `执行中 ${counts.running || 0} · 排队中 ${counts.queued || 0}`),
    createTaskMetric('7 天完成', counts.completed || 0, `失败 ${counts.failed || 0}`),
    createTaskMetric('僵死任务', batch.stuckCount ?? '—', '仅展示，不在此页自动释放', Number(batch.stuckCount) > 0 ? 'delta-warn' : ''),
    createTaskMetric(
      '图片队列',
      `${queue.waiting ?? '—'} / ${queue.running ?? '—'}`,
      `等待 / 执行中 · ${queue.shared ? '共享队列' : '本机队列'}${queue.instances != null ? ` · ${queue.instances} 个实例` : ''}`,
    ),
  );
  const recentCard = createTaskElement('div', 'cc-card');
  recentCard.append(
    createTaskElement('div', 'label', '近期批量任务（本月）'),
    createTaskTable(['任务编号', '用户', '状态', '处理阶段', '站点', '更新时间', '操作'], recentRows, '暂无任务'),
  );
  const stuckCard = createTaskElement('div', 'cc-card');
  stuckCard.append(
    createTaskElement('div', 'label', '僵死任务诊断'),
    createTaskTable(['任务编号', '用户', '处理阶段', '无更新时长', '仍在执行'], stuckRows, '当前无僵死任务'),
  );
  const tables = createTaskElement('div', 'cc-grid-2');
  tables.append(recentCard, stuckCard);
  root.replaceChildren(createTaskFilter(filters, batch.total), metrics, tables);
}
